import { Share, Smartphone, X } from "lucide-react";
import { useStore, useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { useInstallPrompt } from "./installPrompt";

export const INSTALL_BANNER_DISMISSED_KEY = "pwa.installBannerDismissed";

/**
 * Mobile banner offering to add the app to the home screen. Hidden once
 * installed, when the browser cannot install it, or after the user dismisses it
 * (the install entry stays available in the settings). The dismissal lasts until
 * the next login: logging out resets the store, which clears it.
 */
export const InstallBanner = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const { title } = useConfigurationContext();
  const { installMode, promptInstall } = useInstallPrompt();
  const [isDismissed, setDismissed] = useStore(
    INSTALL_BANNER_DISMISSED_KEY,
    false,
  );

  if (!isMobile || isDismissed || installMode === null) return null;

  return (
    <aside
      aria-label={translate("crm.pwa.banner_title", { name: title })}
      className="fixed inset-x-2 bottom-16 z-40 flex items-start gap-3 rounded-lg border bg-background p-3 shadow-lg"
    >
      <Smartphone className="size-5 shrink-0 mt-0.5 text-muted-foreground" />
      <div className="flex-1 space-y-1">
        <p className="text-sm font-medium">
          {translate("crm.pwa.banner_title", { name: title })}
        </p>
        <p className="text-sm text-muted-foreground">
          {installMode === "manual" ? (
            <>
              <Share className="inline size-4 mr-1 align-text-bottom" />
              {translate("crm.pwa.ios_instructions")}
            </>
          ) : (
            translate("crm.pwa.banner_body")
          )}
        </p>
        {installMode === "prompt" && (
          <Button size="sm" onClick={() => void promptInstall()}>
            {translate("crm.pwa.install")}
          </Button>
        )}
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="size-8 shrink-0"
        aria-label={translate("crm.pwa.dismiss")}
        onClick={() => setDismissed(true)}
      >
        <X />
      </Button>
    </aside>
  );
};
